/**
 * Импорт электронного чека: защита от двойного учёта.
 *
 * Документ из другого приложения приходит без гарантий: тот же чек можно
 * загрузить дважды, он может относиться к уже записанной трате, а может быть
 * возвратом или банковской квитанцией. Ошибка в любую сторону стоит дорого —
 * либо покупка учтётся дважды, либо исчезнет.
 *
 * Отсюда три правила, которые здесь и живут:
 *
 *   1. точный повтор открывает прежний результат и **не** создаёт операцию;
 *   2. совпадение суммы само по себе ничего не доказывает — это повод
 *      предложить связь, а не выполнить её;
 *   3. привязка к существующему расходу дополняет его позициями и баланс
 *      второй раз не двигает.
 */
import { ZERO, clampPos, sub, type Money } from './money.js';
import { diffDays, type PlainDate } from './date.js';

/** Что нашлось в документе. Неизвестное остаётся пустым, а не выдумывается. */
export interface ImportedReceipt {
  /** Хеш файла: точный повтор ловится до всякого разбора. */
  fileHash: string;
  /** Фискальные реквизиты, если они есть: самый надёжный ключ. */
  fiscal?: { fn?: string; fd?: string; fp?: string };
  merchant?: string;
  date?: PlainDate;
  time?: string;
  total?: Money;
  items?: { name: string; qty: number; price: Money; sum: Money }[];
  /** Что это за документ. Возврат, квитанция и выписка расходом не являются. */
  kind: 'sale' | 'refund' | 'order' | 'bank_slip' | 'statement' | 'unknown';
}

/** Ранее загруженный документ — для поиска повторов. */
export interface KnownReceipt {
  id: string;
  fileHash?: string;
  fiscal?: { fn?: string; fd?: string; fp?: string };
  txId?: string;
}

/** Операция, к которой документ может относиться. */
export interface ExpenseCandidate {
  id: string;
  date: PlainDate;
  amount: Money;
  merchant?: string;
  /** У операции уже есть чек: второй к ней не привязывается молча. */
  receiptId?: string;
  /** Баланс уже учитывал эту трату (§ FIX-4). */
  balanceApplied?: boolean;
}

export type ImportVerdict =
  /** Точный повтор: открываем прежний результат, ничего не создаём. */
  | { kind: 'duplicate'; receiptId: string; reason: 'file' | 'fiscal' }
  /** Документ не расходный: спрашиваем человека, а не заводим трату. */
  | { kind: 'not_expense'; documentKind: ImportedReceipt['kind']; message: string }
  /** Есть похожие траты: предлагаем связать, но решает человек. */
  | { kind: 'may_match'; candidates: MatchCandidate[] }
  /** Новая покупка: создаём после подтверждения. */
  | { kind: 'new' }
  /** Разобрать не удалось: трату из нераспознанного итога не создаём. */
  | { kind: 'unusable'; message: string };

export interface MatchCandidate {
  expenseId: string;
  /** Насколько уверенно совпало: 0..1. Показывается как порядок, не как число. */
  score: number;
  why: string;
}

/** Насколько разной может быть сумма, чтобы это всё ещё была та же покупка. */
const AMOUNT_TOLERANCE = 100 as Money;   // рубль: округление в приложениях
/** Сколько дней между документом и операцией ещё допустимо. */
const DAY_TOLERANCE = 2;

/**
 * Что делать с загруженным документом.
 *
 * Порядок проверок неслучаен: сначала то, что можно установить точно, и лишь
 * потом — то, что остаётся догадкой и требует решения человека.
 */
export function classifyImport(input: {
  receipt: ImportedReceipt;
  known: readonly KnownReceipt[];
  expenses: readonly ExpenseCandidate[];
}): ImportVerdict {
  const { receipt } = input;

  // 1. Точный повтор файла. Дешевле и надёжнее любого сравнения по сумме.
  const sameFile = input.known.find((k) => k.fileHash && k.fileHash === receipt.fileHash);
  if (sameFile) return { kind: 'duplicate', receiptId: sameFile.id, reason: 'file' };

  // 2. Те же фискальные реквизиты: файл мог быть пересохранён, чек тот же.
  if (receipt.fiscal?.fn && receipt.fiscal.fd) {
    const sameFiscal = input.known.find((k) => k.fiscal?.fn === receipt.fiscal!.fn
      && k.fiscal?.fd === receipt.fiscal!.fd);
    if (sameFiscal) return { kind: 'duplicate', receiptId: sameFiscal.id, reason: 'fiscal' };
  }

  // 3. Не расходный документ. Возврат уменьшает трату, заказ ещё не оплачен,
  //    банковская квитанция — перевод. Ни один из них не расход.
  if (receipt.kind !== 'sale') {
    return {
      kind: 'not_expense',
      documentKind: receipt.kind,
      message: receipt.kind === 'refund'
        ? 'Это возврат, а не покупка — уточни, к какой трате он относится'
        : receipt.kind === 'order'
          ? 'Это заказ, а не кассовый чек: оплата могла не пройти'
          : receipt.kind === 'bank_slip'
            ? 'Это банковская квитанция о переводе, а не чек покупки'
            // Список операций — самая опасная ошибка из возможных: сложив его
            // строки, приложение записало бы одну трату на сумму пяти разных
            // покупок, которых человек второй раз не делал.
            : receipt.kind === 'statement'
              ? 'Это список операций, а не одна покупка — открой нужную и загрузи её'
              : 'Не удалось понять тип документа — проверь, что это кассовый чек',
    };
  }

  // 4. Без итога создавать трату нечего: выдуманная сумма хуже её отсутствия.
  if (receipt.total === undefined || receipt.total <= ZERO) {
    return { kind: 'unusable', message: 'В документе не нашёлся итог — введи сумму вручную' };
  }

  // 5. Похожие траты. Совпадение суммы — повод спросить, а не связать.
  const candidates = matchExpenses(receipt, input.expenses);
  if (candidates.length > 0) return { kind: 'may_match', candidates };

  return { kind: 'new' };
}

/** Похожие операции по сумме, дате и продавцу — по убыванию уверенности. */
export function matchExpenses(
  receipt: ImportedReceipt,
  expenses: readonly ExpenseCandidate[],
): MatchCandidate[] {
  if (receipt.total === undefined) return [];
  const out: MatchCandidate[] = [];

  for (const e of expenses) {
    // К операции с чеком второй документ молча не привязывается.
    if (e.receiptId) continue;

    const amountOff = Math.abs(sub(e.amount, receipt.total));
    if (amountOff > AMOUNT_TOLERANCE) continue;

    const daysOff = receipt.date ? Math.abs(diffDays(receipt.date, e.date)) : DAY_TOLERANCE + 1;
    if (daysOff > DAY_TOLERANCE) continue;

    const sameMerchant = Boolean(receipt.merchant && e.merchant
      && e.merchant.toLowerCase().includes(receipt.merchant.toLowerCase().slice(0, 6)));

    // Сумма и день — уже похоже; продавец добавляет уверенности, но точного
    // совпадения не даёт: в один день в одном месте бывает две покупки.
    const score = (amountOff === ZERO ? 0.5 : 0.35)
      + (daysOff === 0 ? 0.3 : 0.15)
      + (sameMerchant ? 0.2 : 0);

    out.push({
      expenseId: e.id,
      score: Math.min(1, score),
      why: [
        amountOff === ZERO ? 'та же сумма' : 'сумма отличается на копейки',
        daysOff === 0 ? 'тот же день' : `разница ${daysOff} дн.`,
        ...(sameMerchant ? ['тот же продавец'] : []),
      ].join(', '),
    });
  }

  return out.sort((a, b) => b.score - a.score).slice(0, 5);
}

/**
 * Что произойдёт при привязке документа к существующей трате.
 *
 * Документ дополняет покупку позициями и деталями — и **не списывает сумму
 * второй раз**. Если итог чека отличается от записанной суммы, решает человек:
 * молча править запись нельзя, он мог ввести её осознанно.
 */
export interface LinkPlan {
  expenseId: string;
  receiptId: string;
  /** Изменится ли баланс. Всегда `false`: деньги уже учтены. */
  movesBalance: false;
  /** Расхождение итога с записанной суммой, если оно есть. */
  amountDiff: Money;
  /** Что показать человеку перед подтверждением. */
  notes: string[];
}

export function planLink(input: {
  receipt: ImportedReceipt;
  receiptId: string;
  expense: ExpenseCandidate;
}): LinkPlan {
  const diff = input.receipt.total === undefined
    ? ZERO
    : sub(input.receipt.total, input.expense.amount);

  const notes: string[] = [];
  if (diff !== ZERO) {
    notes.push(diff > ZERO
      ? 'В чеке сумма больше записанной — проверь, что это та же покупка'
      : 'В чеке сумма меньше записанной — возможно, часть оплачена иначе');
  }
  if (input.expense.balanceApplied === false) {
    // Такая трата уже учтена в названном остатке: привязка чека этого не
    // меняет, и напоминание здесь нужно, чтобы не «исправили» баланс руками.
    notes.push('Эта трата уже была учтена в названном остатке — баланс не изменится');
  }
  if (input.receipt.items && input.receipt.items.length > 0) {
    notes.push(`Добавится состав: ${input.receipt.items.length} позиций`);
  }

  return {
    expenseId: input.expense.id,
    receiptId: input.receiptId,
    movesBalance: false,
    amountDiff: diff,
    notes,
  };
}

/**
 * Сходится ли состав чека с его итогом.
 *
 * Проверяет код, а не свободный ответ модели: расхождение показывается
 * человеку, а недостающий товар не придумывается.
 */
export function checkItemsTotal(receipt: ImportedReceipt): {
  ok: boolean; itemsSum: Money; diff: Money; message?: string;
} {
  const items = receipt.items ?? [];
  const itemsSum = items.reduce((s, i) => (s + i.sum) as Money, ZERO);
  if (items.length === 0 || receipt.total === undefined) {
    return { ok: true, itemsSum, diff: ZERO };
  }
  const diff = sub(receipt.total, itemsSum);
  if (diff === ZERO) return { ok: true, itemsSum, diff };
  return {
    ok: false,
    itemsSum,
    diff,
    message: diff > ZERO
      ? 'Сумма позиций меньше итога — возможно, часть строк не распозналась'
      : 'Сумма позиций больше итога — вероятно, в чеке есть скидка',
  };
}

/** Остаток, который ещё можно списать по документу. */
export const unmatchedAmount = (receipt: ImportedReceipt, linked: Money): Money =>
  clampPos(sub(receipt.total ?? ZERO, linked));

/* ---------------------------------------------------------------- файл --- */

/** Что мы обещаем принять в первом составе (§20). */
export const SUPPORTED_RECEIPT_TYPES = ['application/pdf', 'image/png', 'image/jpeg'] as const;
export const MAX_RECEIPT_BYTES = 10 * 1024 * 1024;
export const MAX_RECEIPT_PAGES = 10;

export type FileCheck =
  | { ok: true; needsPdfPages: boolean }
  | { ok: false; message: string };

/**
 * Проверка файла до отправки.
 *
 * Форматы и предел размера называются человеку **до** выбора файла, но
 * проверить их всё равно нужно здесь: отказ после загрузки десяти мегабайт по
 * мобильной сети — худший момент для такого сообщения.
 *
 * Тип берётся из содержимого, а не из расширения: переименованный файл не
 * должен попадать в метод, который его не ждёт.
 */
export function checkReceiptFile(file: { type: string; size: number; pages?: number }): FileCheck {
  if (!(SUPPORTED_RECEIPT_TYPES as readonly string[]).includes(file.type)) {
    return { ok: false, message: 'Принимаю PDF, PNG и JPEG — включая скриншот чека' };
  }
  if (file.size > MAX_RECEIPT_BYTES) {
    return { ok: false, message: `Файл больше ${MAX_RECEIPT_BYTES / 1024 / 1024} МБ — пришли документ поменьше` };
  }
  if (file.size === 0) {
    return { ok: false, message: 'Файл пустой — попробуй выбрать ещё раз' };
  }
  if (file.pages !== undefined && file.pages > MAX_RECEIPT_PAGES) {
    return { ok: false, message: `В документе больше ${MAX_RECEIPT_PAGES} страниц — загрузи чеки по отдельности` };
  }
  // PDF нельзя отдать методу, принимающему изображение, просто сменив
  // расширение: страницы нужно подготовить отдельно.
  return { ok: true, needsPdfPages: file.type === 'application/pdf' };
}

/* ---------------------------------------------------------------- квота --- */

export type ImportOutcome =
  | 'parsed'        // разобран новый уникальный чек
  | 'duplicate'     // точный повтор файла или реквизитов
  | 'invalid'       // формат, размер, не тот документ
  | 'failed'        // ошибка обработки
  | 'cancelled';    // человек не подтвердил

/**
 * Расходует ли попытка единицу пакета чеков.
 *
 * Платим за полученную работу, а не за нажатие: невалидный документ и сбой
 * обработки человек не заказывал, а точный повтор мы уже разбирали раньше.
 * Отмена после успешного разбора единицу расходует — работа была сделана,
 * и повтор того же файла уже не спишет второй раз.
 */
export const chargesQuota = (outcome: ImportOutcome): boolean =>
  outcome === 'parsed' || outcome === 'cancelled';
